import { observer } from "mobx-react-lite"
import { useParams } from "react-router"
import { APIQueueTaskKind, QueueTaskMetadataMap } from "@visorpro/client"
import { useCallback, useMemo, useState } from "react"
import { useInterval } from "react-use"
import { MRT_ExpandedState } from "material-react-table"

import { Stores } from "../../../../util/store"
import { QueueTaskDetailPanel, QueueTasksTable } from "../../../../components/tasks-table"
import { useQueueTasks } from "../../../../stores/queue-tasks-store"
import { useOrganizations } from "../../../../stores/organization-store"

export interface OrganizationQueueTasksPageProps {
    stores: Stores
}
const INTERVAL_DELAY = 30000

const OrganizationQueueTasksPage = observer(({ stores }: OrganizationQueueTasksPageProps) => {
    const { organizationId } = useParams()
    const [pagination, setPagination] = useState({})
    const [filters, setFilters] = useState({})
    const [expanded, setExpanded] = useState<MRT_ExpandedState>({})
    const organizations = useOrganizations(stores.organizations)

    const options = useMemo(() => {
        return {
            ...pagination,
            ...filters,
            metadata: { organization_id: organizationId }
        }
    }, [pagination, filters, organizationId])
    if (!organizationId) return

    const tasksStore = useMemo(
        () => stores.organizations.organizationQueueTasksStoresById[organizationId],
        [organizationId, stores.organizations],
    )
    const tasks = useQueueTasks(tasksStore, options)

    const createExportDisQueueTask = async (metadata: QueueTaskMetadataMap[APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS]) => {
        await stores.queueTasks.create({
            kind: APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS,
            metadata: { ...metadata, organization_id: organizationId },
        })
        await tasksStore.list(options)
    }

    const createExportHbsQueueTask = async (metadata: QueueTaskMetadataMap[APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS]) => {
        await stores.queueTasks.create({
            kind: APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS,
            metadata: { ...metadata, organization_id: organizationId },
        })
        await tasksStore.list(options)
    }

    useInterval(() => {
        if (!tasksStore.isFetching) {
            void tasksStore.list(options)
        }
    }, INTERVAL_DELAY)

    const renderDetailPanel = useCallback(
        (taskId: string) => {
            if (expanded === true || !expanded[taskId]) {
                return null
            }

            return (
                <QueueTaskDetailPanel store={tasksStore} taskId={taskId} />
            )
        },
        [expanded, tasksStore],
    )

    return (
        <div data-testid="org-tasks-page" style={{ display: 'contents' }}>
            <QueueTasksTable
                tasks={tasks}
                organizations={organizations}
                isLoading={
                    tasksStore.isFetching && !tasksStore.isFetched
                }
                isCreating={tasksStore.isCreating}
                pagination={pagination}
                setPagination={setPagination}
                filters={filters}
                setFilters={setFilters}
                expanded={expanded}
                onExpandedChange={setExpanded}
                renderDetailPanel={renderDetailPanel}
                createQueueTaskMappings={{
                    [APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS]: createExportDisQueueTask,
                    [APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS]: createExportHbsQueueTask,
                }}
            />
        </div>
    )
})

export default OrganizationQueueTasksPage
